import { useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { StyledHighlightedDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/dropdown/styles";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { ScrollDismissBoundary } from "app/pages/sales-intelligence/common-components/scroll-dismiss/ScrollDismissBoundary";
import type { SignalFilters, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import SignalsSegmentedName from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsSelectDropdown/SignalsSegmentedName";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

import { StyledDropdownParent } from "./styles";

type Props = {
    selectedUISignals: readonly UISignal<SignalFilters>[];
    signalType: SignalType;
    signals: readonly UISignal<SignalFilters>[];
    onSignalSelect: (uiSignal: UISignal<SignalFilters>) => void;
};

const SignalSelectGroup = ({ signalType, selectedUISignals, signals, onSignalSelect }: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Stack
            data-automation="signal-select-group"
            data-automation-type={signalType}
            gap="8px"
        >
            <ScrollDismissBoundary
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            >
                <Combobox
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    placement="bottom-start"
                >
                    <Combobox.Trigger>
                        <StyledDropdownParent>
                            <StyledHighlightedDropdownButtonWrap>
                                <DropdownButton hasValue={false}>
                                    <I18n
                                        data-automation="signal-dd-button-text"
                                        translatable={{ key: signalsI18nKeys.filters.selectPlaceholder }}
                                    />
                                </DropdownButton>
                            </StyledHighlightedDropdownButtonWrap>
                        </StyledDropdownParent>
                    </Combobox.Trigger>

                    <Combobox.Content selectionMode="multiple">
                        {signals.map((uiSignal) => {
                            const isSelected = selectedUISignals.some((selected) => selected.id === uiSignal.id);

                            return (
                                <Combobox.Option
                                    key={uiSignal.id}
                                    id={uiSignal.id}
                                    textValue={uiSignal.id}
                                    isSelected={isSelected}
                                    onClick={() => onSignalSelect(uiSignal)}
                                >
                                    <Flex
                                        alignItems="center"
                                        justifyContent="space-between"
                                        gap="8px"
                                        width="100%"
                                    >
                                        <Stack gap="6px">
                                            <SignalsSegmentedName uiSignal={uiSignal} />
                                        </Stack>
                                        <Combobox.OptionCheckbox isChecked={isSelected} />
                                    </Flex>
                                </Combobox.Option>
                            );
                        })}
                    </Combobox.Content>
                </Combobox>
            </ScrollDismissBoundary>
        </Stack>
    );
};

export default SignalSelectGroup;
